import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { SyncLoader } from 'react-spinners'

type Props = {
    open: boolean
    /** The work's tag, which is its name, e.g. "instagram:hotelsdotcom:scrape_comment_fetch". */
    tag: string
    onClose: () => void
    onConfirm: () => Promise<void>
    loading: boolean
}

/**
 * Confirmation step before stopping a schedule. Same shape as `DeleteCookieModal`.
 */
function StopScheduledWorkModal({ open, tag, onClose, onConfirm, loading }: Props) {
    if (!open) return null

    return (
        <div
            className="fixed z-10 inset-0 overflow-y-auto"
            aria-labelledby="stop-schedule-modal-title"
            role="dialog"
            aria-modal="true"
        >
            <div className="flex items-end justify-center pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div
                    className="fixed inset-0 bg-ink-500 bg-opacity-75 transition-opacity"
                    aria-hidden="true"
                    onClick={onClose}
                />

                <span
                    className="hidden sm:inline-block sm:align-middle sm:h-screen"
                    aria-hidden="true"
                >
                    &#8203;
                </span>
                <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                    <div className="sm:flex sm:items-start">
                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-severe-100 sm:mx-0 sm:h-10 sm:w-10">
                            <ExclamationTriangleIcon className="h-6 w-6 text-severe-600" />
                        </div>
                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                            <h3
                                className="text-lg leading-6 text-ink-900"
                                id="stop-schedule-modal-title"
                            >
                                Stop schedule
                            </h3>
                            <div className="mt-2">
                                <p className="text-sm text-ink-500">
                                    Stop <span className="font-medium">{tag}</span>? A run
                                    already under way will finish, and nothing will follow it.
                                    There is no resume: to monitor this account again, create the
                                    schedule afresh.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <button
                            type="button"
                            onClick={onConfirm}
                            disabled={loading}
                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-severe-600 text-base text-white hover:bg-severe-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-severe-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                        >
                            {loading && <SyncLoader color="#fff" size="4px" />}
                            {!loading && <>Stop schedule</>}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="mt-3 w-full inline-flex justify-center rounded-md border border-ink-300 shadow-sm px-4 py-2 bg-white text-base text-ink-700 hover:bg-ink-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-arwen-500 sm:mt-0 sm:w-auto sm:text-sm"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default StopScheduledWorkModal
